import React from 'react';
import { fmtHM, fmtLong, fromISO } from '../lib/format.js';
import { Icon } from './Icon.jsx';

/**
 * v2.0 (A6 follow-up, design chat2 answer 7) — read-only entry inspector.
 *
 * When a month is locked, the Edit and Delete affordances are hidden
 * from rows. But the user should still be able to open an entry and see
 * its full description / metadata. This dialog provides that surface —
 * a clean read-only view with every field, no actions.
 */
export function ViewEntryDialog({ entry, onClose }) {
  if (!entry) return null;
  const mins = entry.hours * 60 + entry.minutes;
  return (
    <div className="tt-modal-backdrop" onClick={onClose}>
      <div
        className="tt-modal"
        style={{ maxWidth: 560, marginTop: '8vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            padding: '20px 24px 14px',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            borderBottom: '1px solid var(--sp-border)',
          }}
        >
          <span
            style={{
              width: 32,
              height: 32,
              borderRadius: 10,
              background: 'var(--sp-bg-muted)',
              color: 'var(--sp-fg-2)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 'none',
            }}
          >
            <Icon name="lock" size={14} stroke={2.2} />
          </span>
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>
              Entry details
            </h2>
            <p
              style={{
                margin: '2px 0 0',
                fontSize: 12,
                color: 'var(--sp-fg-3)',
              }}
            >
              Read-only — this month is approved and locked.
            </p>
          </div>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>
            <Icon name="x" size={16} />
          </button>
        </div>
        <div style={{ padding: '4px 24px 12px' }}>
          <Row label="Date">{fmtLong(fromISO(entry.date))}</Row>
          <Row label="Time">
            <span
              style={{
                fontWeight: 600,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {fmtHM(mins)}
            </span>
          </Row>
          <Row label="Activity">{entry.activityName}</Row>
          {entry.product && entry.product !== 'None' && (
            <Row label="Product">{entry.product}</Row>
          )}
          {entry.clientName && <Row label="Client">{entry.clientName}</Row>}
          {entry.ticketId && (
            <Row label="Jira ticket">
              <span style={{ fontWeight: 700, color: '#5E2A9C', marginRight: 6 }}>
                {entry.ticketId}
              </span>
              <span style={{ color: 'var(--sp-fg-2)' }}>{entry.ticketTitle}</span>
            </Row>
          )}
          {entry.epicId && (
            <Row label={entry.ticketId ? 'Parent epic' : 'Jira epic'}>
              <span style={{ fontWeight: 700, color: '#0052CC', marginRight: 6 }}>
                {entry.epicId}
              </span>
              <span style={{ color: 'var(--sp-fg-2)' }}>{entry.epicTitle}</span>
            </Row>
          )}
          {entry.description && (
            <Row label="Description">
              <span style={{ color: 'var(--sp-fg-2)', lineHeight: 1.55 }}>
                {entry.description}
              </span>
            </Row>
          )}
          <Row label="Source">
            <span style={{ textTransform: 'capitalize' }}>
              {entry.source || 'manual'}
            </span>
          </Row>
        </div>
        <div
          style={{
            padding: '12px 24px 18px',
            borderTop: '1px solid var(--sp-border-soft)',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <button className="btn btn-primary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '120px 1fr',
        gap: 12,
        padding: '10px 0',
        borderBottom: '1px solid var(--sp-border-soft)',
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: 'var(--sp-fg-3)',
          alignSelf: 'center',
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 14, color: 'var(--sp-fg-1)' }}>{children}</div>
    </div>
  );
}
